import {i18n} from '@online-offer/i18n';
import {type MoneyResponseBody} from '@online-offer/shared';
import {Show, Text, VStack} from 'platform/foundation';
import {useFormatCurrency} from 'platform/locale';
import {match} from 'ts-pattern';

import {useState} from 'react';

import {isNil, isNotNil, uniq} from 'ramda';

import {EMPTY_PLACEHOLDER, Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {
  calculateShareOfVehiclePrice,
  constrainFinancingSelection,
  type DownPaymentAdjustment,
  getDownPaymentAdjustment,
  getFinancingControlLimits,
} from 'features/financial-products';

import {
  FinancialProductOffer,
  FinancialProductPreferenceCoordinates,
  LeasingTargetGroup,
} from '../../api/types/onlineOfferTypes';
import {RadioButtonGroup} from '../../components/RadioButtonGroup/RadioButtonGroup';
import {ChoiceChips} from './ChoiceChips';
import {ChoiceSlider} from './ChoiceSlider';
import {formatFinancialProductPercentage} from './formatFinancialProductPercentage';
import {getLeasingTargetGroupLabelKey} from './getLeasingTargetGroupLabelKey';

interface FinancialProductControlsProps extends TestIdProps {
  offer: FinancialProductOffer;
  coordinates: FinancialProductPreferenceCoordinates;
  vehiclePriceWithVat: MoneyResponseBody | Nullish;
  onChange: (coordinates: FinancialProductPreferenceCoordinates) => void;
  onPreview: (coordinates: FinancialProductPreferenceCoordinates) => void;
}

/**
 * Financing is continuous within the selected term's brackets; leasing uses exact matrix cells.
 * A dragged slider only previews its position; the choice is reported once released, so a drag
 * costs one write, not one per step. A term or balloon change carries the down payment and
 * balloon that term offers in that same write (and preview), so the customer never sends a
 * combination the offer refuses.
 */
export function FinancialProductControls(props: FinancialProductControlsProps) {
  const formatCurrency = useFormatCurrency();
  const [downPaymentAdjustment, setDownPaymentAdjustment] = useState<DownPaymentAdjustment | null>(
    null
  );
  const price = props.vehiclePriceWithVat;

  // Rounded as the backend rounds it, so the label equals the quoted down payment or balloon.
  const formatShareOfPrice = isNil(price)
    ? undefined
    : (percentage: number) => {
        const share = calculateShareOfVehiclePrice(price, percentage);

        return isNil(share)
          ? EMPTY_PLACEHOLDER
          : (formatCurrency(parseFloat(share.amount), share.currency) ?? EMPTY_PLACEHOLDER);
      };

  const formatMonths = (value: number) => i18n.t('financialProducts.monthsValue', {value});

  const update = (changes: Partial<FinancialProductPreferenceCoordinates>) =>
    props.onChange({...props.coordinates, ...changes});

  const preview = (changes: Partial<FinancialProductPreferenceCoordinates>) =>
    props.onPreview({...props.coordinates, ...changes});

  const constrain = (changes: Partial<FinancialProductPreferenceCoordinates>) =>
    constrainFinancingSelection(props.offer, {...props.coordinates, ...changes});

  const updateConstrained = (changes: Partial<FinancialProductPreferenceCoordinates>) => {
    const requested = {...props.coordinates, ...changes};
    const constrained = constrain(changes);

    setDownPaymentAdjustment(getDownPaymentAdjustment(requested, constrained));
    props.onChange(constrained);
  };

  const financingLimits = getFinancingControlLimits(props.offer, props.coordinates);
  const downPaymentLimits = financingLimits.downPayment;

  const leasingTerms = uniq(props.offer.availableLeasingCells.map((cell) => cell.termMonths)).sort(
    (left, right) => left - right
  );
  const mileagesForTerm = (termMonths: number | null) =>
    props.offer.availableLeasingCells
      .filter((cell) => cell.termMonths === termMonths)
      .map((cell) => cell.annualMileage);

  const toLeasingTerm = (termMonths: number | null) => {
    const mileages = mileagesForTerm(termMonths);
    const keepsMileage =
      isNotNil(props.coordinates.annualMileage) &&
      mileages.includes(props.coordinates.annualMileage);

    return {
      termMonths,
      annualMileage: keepsMileage ? props.coordinates.annualMileage : (mileages.at(0) ?? null),
    };
  };

  return match(props.offer.productType)
    .with('LEASING', () => (
      <VStack spacing={4}>
        <Show when={props.offer.availableTargetGroups.length > 1}>
          <RadioButtonGroup<LeasingTargetGroup>
            appearance="compact"
            label={i18n.t('financialProducts.customerType')}
            value={props.coordinates.leasingTargetGroup ?? 'PRIVATE'}
            options={props.offer.availableTargetGroups.map((targetGroup) => ({
              value: targetGroup,
              label: i18n.t(getLeasingTargetGroupLabelKey(targetGroup)),
            }))}
            onChange={(leasingTargetGroup) => update({leasingTargetGroup})}
            data-testid={suffixTestId('targetGroup', props)}
          />
        </Show>
        <Show when={leasingTerms.length > 0}>
          <ChoiceChips
            label={i18n.t('financialProducts.leasingTerm')}
            value={props.coordinates.termMonths ?? leasingTerms.at(0) ?? 0}
            choices={leasingTerms}
            formatValue={formatMonths}
            onChange={(termMonths) => update(toLeasingTerm(termMonths))}
            data-testid={suffixTestId('leasingTerm', props)}
          />
          <ChoiceSlider
            key={props.coordinates.termMonths}
            label={i18n.t('financialProducts.annualMileage')}
            value={props.coordinates.annualMileage ?? 0}
            choices={uniq(mileagesForTerm(props.coordinates.termMonths)).sort(
              (left, right) => left - right
            )}
            formatChoice={(annualMileage) =>
              i18n.t('financialProducts.kilometresPerYearValue', {value: annualMileage})
            }
            onDrag={(annualMileage) => preview({annualMileage})}
            onChangeEnd={(annualMileage) => update({annualMileage})}
            isDiscrete
            data-testid={suffixTestId('annualMileage', props)}
          />
        </Show>
      </VStack>
    ))
    .otherwise(() => (
      <VStack spacing={4}>
        <Show when={financingLimits.terms.length > 0}>
          <ChoiceChips
            label={i18n.t('financialProducts.term')}
            choices={financingLimits.terms}
            value={props.coordinates.termMonths ?? financingLimits.terms.at(0) ?? 0}
            formatValue={formatMonths}
            onChange={(termMonths) => updateConstrained({termMonths})}
            data-testid={suffixTestId('term', props)}
          />
        </Show>
        <Show when={isNotNil(downPaymentLimits)}>
          <ChoiceSlider
            label={i18n.t('financialProducts.downPayment')}
            choices={downPaymentLimits?.ticks ?? []}
            emphasizedChoices={downPaymentLimits?.brackets}
            step={props.offer.downPaymentStep}
            value={props.coordinates.downPaymentPercentage ?? downPaymentLimits?.min ?? 0}
            formatChoice={formatFinancialProductPercentage}
            formatAmount={formatShareOfPrice}
            onDrag={(downPaymentPercentage) => preview({downPaymentPercentage})}
            onChangeEnd={(downPaymentPercentage) => {
              setDownPaymentAdjustment(null);
              update({downPaymentPercentage});
            }}
            data-testid={suffixTestId('downPayment', props)}
          />
        </Show>
        <Show when={isNotNil(downPaymentAdjustment)}>
          <span aria-live="polite" data-testid={suffixTestId('downPaymentAdjustment', props)}>
            <Text size="small" color="warning">
              {downPaymentAdjustment?.reason === 'FLOOR'
                ? i18n.t('financialProducts.downPaymentRaisedToFloor', {
                    from: downPaymentAdjustment.fromPercentage,
                    to: downPaymentAdjustment.toPercentage,
                    term: props.coordinates.termMonths,
                  })
                : i18n.t('financialProducts.downPaymentLoweredForBalloon', {
                    from: downPaymentAdjustment?.fromPercentage,
                    to: downPaymentAdjustment?.toPercentage,
                  })}
            </Text>
          </span>
        </Show>
        <Show when={financingLimits.balloonPaymentPercentages.length > 0}>
          <ChoiceSlider
            label={i18n.t('financialProducts.balloonPayment')}
            choices={financingLimits.balloonPaymentPercentages}
            value={
              props.coordinates.balloonPaymentPercentage ??
              financingLimits.balloonPaymentPercentages.at(0) ??
              0
            }
            formatChoice={formatFinancialProductPercentage}
            formatAmount={formatShareOfPrice}
            onDrag={(balloonPaymentPercentage) =>
              props.onPreview(constrain({balloonPaymentPercentage}))
            }
            onChangeEnd={(balloonPaymentPercentage) =>
              updateConstrained({balloonPaymentPercentage})
            }
            data-testid={suffixTestId('balloonPayment', props)}
          />
        </Show>
      </VStack>
    ));
}
